'use client'

import Link from 'next/link'
import { ArrowRight, Sparkles, Tag, ShieldCheck, PawPrint, ChevronRight } from 'lucide-react'
import Image from 'next/image'
import { getBaseImageUrl } from '@/constants/hooks'

const C = {
    PRIMARY: '#F8F246',
    BROWN: '#8C5826',
    SECONDARY: '#2CBBC3',
    ORANGE: '#FF9934',
    BG: '#fffaf5',
    BORDER: '#f0ede8',
}

interface Props {
    companyId: string
    companyName: string
    siteDescription: string
    companyDomain: string
    mer_slug: string
    slug_type?: string
    cat_slug?: string
    heroBadge?: string
    heroHeadline?: string
    topDealTitle?: string
    topDealSubtitle?: string
    totalStores?: number
    totalDeals?: number
    totalCategories?: number
    topStores?: { merchant_name: string; merchant_logo: string; slug: string }[]
    topCategories?: { name: string; url?: string; slug?: string }[]
}

const PetCareHero = ({
    companyId,
    companyName,
    siteDescription,
    companyDomain,
    mer_slug,
    slug_type = '',
    cat_slug = 'category',
    heroBadge,
    heroHeadline,
    topDealTitle,
    topDealSubtitle,
    totalStores = 0,
    totalDeals = 0,
    totalCategories = 0,
    topStores = [],
    topCategories = [],
}: Props) => {
    const badge = heroBadge || `${companyName} Deals`
    const headline = heroHeadline || `Best Deals from ${companyName}`
    const dealTitle = topDealTitle || 'Top Deals Today'
    const dealSubtitle = topDealSubtitle || siteDescription || `Exclusive coupons from ${companyName}`
    const description = siteDescription || `Discover exclusive coupons & discounts from ${companyName}`

    const stats = [
        { label: 'Verified Stores', value: totalStores > 0 ? `${totalStores}+` : '150+' },
        { label: 'Active Deals', value: totalDeals > 0 ? `${totalDeals}+` : '2,000+' },
        { label: 'Categories', value: totalCategories > 0 ? `${totalCategories}+` : '40+' },
        { label: 'Savings Rate', value: 'Up to 70%' },
    ]

    return (
        <section
            className="relative overflow-hidden"
            style={{ background: 'linear-gradient(130deg, #fffaf5 0%, #fff7ee 50%, #fef0dc 100%)' }}
        >


            {/* ── Radial glows ── */}
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-25 pointer-events-none"
                style={{ background: `radial-gradient(circle, #ffe0a0 0%, transparent 65%)` }} />
            <div className="absolute -bottom-20 -left-20 w-[360px] h-[360px] rounded-full opacity-15 pointer-events-none"
                style={{ background: `radial-gradient(circle, ${C.SECONDARY}60 0%, transparent 65%)` }} />

            {/* ── Dot pattern ── */}
            <div className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: `radial-gradient(${C.BROWN} 1px, transparent 1px)`,
                    backgroundSize: '30px 30px',
                    opacity: 0.035,
                }} />

            {/* ── Decorative pet emojis ── */}
            <span className="absolute select-none pointer-events-none leading-none"
                style={{ fontSize: '3rem', top: '6%', right: '4%', opacity: 0.18, filter: 'blur(0.5px) drop-shadow(0 0 0 transparent)', transform: 'rotate(10deg)' }}>
                🐶
            </span>
            <span className="absolute select-none pointer-events-none leading-none"
                style={{ fontSize: '2.5rem', bottom: '12%', right: '20%', opacity: 0.15, filter: 'blur(0.5px) drop-shadow(0 0 0 transparent)', transform: 'rotate(-8deg)' }}>
                🐱
            </span>
            <span className="absolute select-none pointer-events-none leading-none"
                style={{ fontSize: '2.2rem', top: '22%', left: '0.5%', opacity: 0.15, filter: 'blur(0.5px) drop-shadow(0 0 0 transparent)', transform: 'rotate(-12deg)' }}>
                🐾
            </span>
            <span className="absolute select-none pointer-events-none leading-none"
                style={{ fontSize: '2rem', bottom: '10%', left: '4%', opacity: 0.13, filter: 'blur(0.5px) drop-shadow(0 0 0 transparent)', transform: 'rotate(6deg)' }}>
                🦴
            </span>

            {/* ── Main Content ── */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 py-12 lg:py-20">

                    {/* ════ LEFT ════ */}
                    <div className="flex-1 min-w-0 space-y-6">

                        {/* Eyebrow badge */}
                        <div
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border shadow-xs"
                            style={{
                                background: `${C.PRIMARY}20`,
                                borderColor: `${C.PRIMARY}50`,
                                color: C.BROWN,
                            }}
                        >
                            <PawPrint size={11} />
                            {badge}
                        </div>

                        {/* Headline */}
                        <div className="space-y-3">
                            <h1
                                className="font-black leading-[1.08] tracking-tight text-gray-900"
                                style={{ fontSize: 'clamp(2rem, 4.8vw, 3.75rem)' }}
                            >
                                {headline}
                            </h1>
                            <p className="text-sm sm:text-[15px] text-gray-500 max-w-[500px] leading-relaxed font-medium">
                                {description}
                            </p>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                href={`/${mer_slug}`}
                                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl text-[13px] font-black uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                                style={{
                                    background: C.BROWN,
                                    color: '#ffffff',
                                    boxShadow: `0 6px 20px rgba(140,88,38,0.28)`,
                                }}
                            >
                                Shop All Deals
                                <ArrowRight size={14} />
                            </Link>
                            <Link
                                href={`/${cat_slug}`}
                                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl text-[13px] font-bold border-2 transition-all duration-200 hover:-translate-y-0.5 bg-white/60 backdrop-blur-sm hover:bg-white"
                                style={{ borderColor: `#e5e7eb`, color: '#374151' }}
                            >
                                Browse Categories
                                <ChevronRight size={14} />
                            </Link>
                        </div>

                        {/* Trust strip */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                                <ShieldCheck size={13} className="text-emerald-500" /> All deals verified
                            </span>
                            <span className="w-px h-3 bg-gray-300 hidden sm:block" />
                            <span className="inline-flex items-center gap-1.5">
                                <Tag size={12} style={{ color: C.SECONDARY }} /> Free to use
                            </span>
                        </div>

                        {/* Category pills */}
                        {topCategories.length > 0 && (
                            <div className="space-y-2.5">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                                    Popular Categories
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {topCategories.slice(0, 8).map((cat, i) => {
                                        const rawSlug = cat.slug || cat.url?.replace(/^\/?(?:category\/)?/, '') || cat.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                                        const prefix = cat_slug ? `/${cat_slug}` : '/category'
                                        const href = cat.url ? (cat.url.startsWith('/') ? cat.url : `/${cat.url}`) : `${prefix}/${rawSlug}`
                                        return (
                                            <Link
                                                key={i}
                                                href={href}
                                                className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[12px] font-semibold bg-white border border-[#f0ede8] text-gray-700 hover:text-[#8C5826] hover:border-[#8C5826]/30 hover:bg-[#fffbf0] transition-all shadow-2xs"
                                            >
                                                {cat.name}
                                            </Link>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ════ RIGHT ════ */}
                    <div className="hidden lg:flex flex-col gap-4 w-[360px] shrink-0">

                        {/* Featured deal card */}
                        <div
                            className="relative rounded-3xl p-7 overflow-hidden"
                            style={{
                                background: `linear-gradient(140deg, ${C.BROWN} 0%, #a86030 60%, ${C.ORANGE} 100%)`,
                                boxShadow: `0 20px 52px rgba(140,88,38,0.28)`,
                            }}
                        >
                            {/* circle accents */}
                            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full opacity-10"
                                style={{ background: C.PRIMARY }} />
                            <div className="absolute -bottom-12 -left-8 w-44 h-44 rounded-full opacity-[0.07]"
                                style={{ background: '#ffffff' }} />

                            {/* Subtle large emoji in card — intentional art, not decoration */}
                            <span className="absolute right-5 bottom-4 text-7xl select-none pointer-events-none"
                                style={{ opacity: 0.12, filter: 'blur(1px)', transform: 'rotate(12deg)' }}>
                                🐾
                            </span>

                            <div className="relative z-10 space-y-4">
                                <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest"
                                    style={{ background: C.PRIMARY, color: C.BROWN }}
                                >
                                    <Sparkles size={9} /> {companyName}
                                </span>

                                <div>
                                    <p className="text-2xl font-black text-white leading-snug">{dealTitle}</p>
                                    <p className="text-white/70 text-sm mt-1.5 leading-relaxed line-clamp-2">{dealSubtitle}</p>
                                </div>

                                <Link
                                    href={`/${mer_slug}`}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] font-black transition-all hover:-translate-y-0.5"
                                    style={{ background: '#ffffff', color: C.BROWN, boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}
                                >
                                    View All Deals <ArrowRight size={12} />
                                </Link>
                            </div>
                        </div>

                        {/* Top stores card */}
                        {topStores.length > 0 && (
                            <div className="bg-white rounded-2xl border border-[#f0ede8] shadow-xs overflow-hidden">
                                <div className="px-4 py-3 border-b border-[#f0ede8] flex items-center justify-between">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                                        Featured Stores
                                    </p>
                                    <Link href={`/${mer_slug}`} className="text-[10px] font-bold text-[#8C5826] hover:underline">
                                        View all →
                                    </Link>
                                </div>
                                <div className="p-3 grid grid-cols-3 gap-2">
                                    {topStores.slice(0, 6).map((store, i) => (
                                        <Link
                                            key={i}
                                            href={`/${mer_slug}/${store.slug}`}
                                            className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-[#f0ede8] hover:border-[#8C5826]/25 hover:bg-[#fffbf0] transition-all group"
                                        >
                                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                                                <Image
                                                    src={getBaseImageUrl(companyDomain, store.merchant_logo, '')}
                                                    alt={store.merchant_name}
                                                    width={40}
                                                    height={40}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-[9px] font-semibold text-gray-500 group-hover:text-[#8C5826] truncate w-full text-center transition-colors">
                                                {store.merchant_name}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Stats bar ── */}
                <div className="border-t border-[#f0ede8] grid grid-cols-2 sm:grid-cols-4 divide-x divide-[#f0ede8] py-5">
                    {stats.map((s, i) => (
                        <div key={i} className="flex flex-col items-center gap-0.5 text-center px-4 py-2 sm:py-0">
                            <span className="text-xl sm:text-2xl font-black text-gray-900 tabular-nums">{s.value}</span>
                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Bottom soft white fade ── */}
            <div className="h-8 w-full" style={{ background: 'linear-gradient(to bottom, transparent, #ffffff)' }} />
        </section>
    )
}

export default PetCareHero
